import React, { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import AdminLayout from "../components/admin/AdminLayout";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase";
import "../styles/AdminPage.css";

const AdminPage: React.FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inject a robots meta tag so crawlers reaching /admin directly are told
  // not to index. Robots.txt + render.yaml X-Robots-Tag header are the
  // other two layers; this is belt-and-suspenders.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setChecking(false);
      return;
    }
    const supabase = getSupabase();
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setChecking(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { error: signInError } = await getSupabase().auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) setError(signInError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await getSupabase().auth.signOut();
    setSession(null);
  };

  // Wrapper that renders the brand panel on the left for any auth state.
  const AuthShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="admin-auth">
      <aside className="admin-auth-brand">
        <div className="admin-auth-brand-mark">
          <div className="admin-auth-brand-mark-logo">C</div>
          <span className="admin-auth-brand-mark-text">La Cava Grecque</span>
        </div>
        <div className="admin-auth-brand-pitch">
          <h1>Menu Studio</h1>
          <p>
            Edit live prices, manage menus, and publish to your customer-facing
            site — all in one place. Changes flow through Supabase and rebuild the
            static site automatically.
          </p>
        </div>
        <div className="admin-auth-brand-footer">Internal · Staff Only</div>
      </aside>
      <main className="admin-auth-form-wrap">{children}</main>
    </div>
  );

  if (!isSupabaseConfigured) {
    return (
      <AuthShell>
        <div className="admin-auth-form-card">
          <h2>Setup required</h2>
          <p className="sub">Supabase isn't configured yet.</p>
          <div className="admin-auth-config">
            Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> in
            your <code>.env.local</code>, then restart <code>npm run dev</code>.
            See <code>MENU_CMS_PLAN.md</code> for the full setup checklist.
          </div>
        </div>
      </AuthShell>
    );
  }

  if (checking) {
    return (
      <AuthShell>
        <div className="admin-auth-form-card">
          <p className="admin-auth-loading">Checking session...</p>
        </div>
      </AuthShell>
    );
  }

  if (!session) {
    return (
      <AuthShell>
        <div className="admin-auth-form-card">
          <h2>Welcome back</h2>
          <p className="sub">Sign in with your staff account.</p>
          <form className="admin-auth-form" onSubmit={handleLogin}>
            <div>
              <label htmlFor="admin-email">Email</label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lacavagrecque.com"
                autoComplete="username"
                required
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
            <button type="submit" disabled={submitting}>
              {submitting ? "Signing in..." : "Sign in"}
            </button>
            {error && <div className="admin-auth-error">{error}</div>}
          </form>
        </div>
      </AuthShell>
    );
  }

  return <AdminLayout onLogout={handleLogout} userEmail={session.user.email ?? ""} />;
};

export default AdminPage;
